import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { toast } from '../components/Toast'
import Spinner from '../components/Spinner'

type InquiryListItem = {
  id: number
  user_id: number
  category: string
  title: string
  status: string
  created_at: string
}

type InquiryReply = {
  id: number
  inquiry_id: number
  admin_user_id: number | null
  content: string
  created_at: string
}

type InquiryDetail = {
  id: number
  user_id: number
  category: string
  title: string
  content: string
  status: string
  created_at: string
  updated_at: string
  replies: InquiryReply[]
}

const STATUS_BADGE: Record<string, string> = {
  '대기': 'bg-[var(--color-surface)] text-[var(--color-text-tertiary)] border border-[var(--color-border-default)]',
  '처리중': 'bg-[var(--color-accent)]/10 text-[var(--color-accent)] border border-[var(--color-accent)]/30',
  '완료': 'bg-[var(--color-success)]/10 text-[var(--color-success)] border border-[var(--color-success)]/30',
}

const CATEGORY_ICON: Record<string, string> = {
  '문의': '❓',
  '건의': '💡',
  '버그신고': '🐛',
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

function formatDateTime(iso: string) {
  const d = new Date(iso)
  return `${formatDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// ─── 상세 뷰 ────────────────────────────────────────────────────────────────
function DetailView({
  item,
  onBack,
  onDeleted,
}: {
  item: InquiryListItem
  onBack: () => void
  onDeleted: (id: number) => void
}) {
  const [detail, setDetail] = useState<InquiryDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    loadDetail()
  }, [item.id])

  const loadDetail = async () => {
    setLoading(true)
    try {
      const { data } = await client.get<InquiryDetail>(`/inquiries/${item.id}`)
      setDetail(data)
      setEditTitle(data.title)
      setEditContent(data.content)
    } catch {
      toast('상세 내용을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const canEdit = detail ? detail.replies.length === 0 : false

  const handleSave = async () => {
    if (!editTitle.trim()) { toast('제목을 입력해주세요.', 'error'); return }
    if (!editContent.trim()) { toast('내용을 입력해주세요.', 'error'); return }
    setSaving(true)
    try {
      const { data } = await client.put<InquiryDetail>(`/inquiries/${item.id}`, {
        title: editTitle.trim(),
        content: editContent.trim(),
      })
      setDetail(data)
      setEditing(false)
      toast('수정되었습니다.', 'success')
    } catch (e: any) {
      toast(e?.response?.data?.detail || '수정에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await client.delete(`/inquiries/${item.id}`)
      toast('삭제되었습니다.', 'success')
      onDeleted(item.id)
    } catch (e: any) {
      toast(e?.response?.data?.detail || '삭제에 실패했습니다.', 'error')
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  const inputCls =
    'w-full bg-[var(--color-bg)] border border-[var(--color-border-default)] rounded-lg px-4 py-2.5 text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-border-accent)] transition-colors text-sm resize-none'

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">문의 상세</h1>
          <p className="text-[var(--color-text-tertiary)] text-xs">내 문의 내역 › 상세 보기</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : detail ? (
        <>
          {/* 본문 */}
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">{CATEGORY_ICON[detail.category] ?? ''}</span>
                <span className="text-xs text-[var(--color-text-tertiary)]">{detail.category}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[detail.status] ?? STATUS_BADGE['대기']}`}>
                  {detail.status}
                </span>
              </div>
              <span className="text-xs text-[var(--color-text-disabled)]">{formatDateTime(detail.created_at)}</span>
            </div>

            {editing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  className={inputCls}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  maxLength={100}
                />
                <textarea
                  className={inputCls}
                  rows={6}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  maxLength={2000}
                />
                <div className="flex gap-2">
                  <button onClick={() => setEditing(false)}
                    className="flex-1 py-2 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                    취소
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex-1 py-2 bg-[var(--color-accent)] hover:opacity-90 disabled:opacity-50 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity">
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-[var(--color-text-primary)] font-semibold text-base mb-3">{detail.title}</h2>
                <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed whitespace-pre-wrap">{detail.content}</p>

                {canEdit && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--color-border-default)]">
                    <button onClick={() => setEditing(true)}
                      className="flex-1 py-2 border border-[var(--color-border-default)] text-[var(--color-text-tertiary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                      수정
                    </button>
                    <button onClick={() => setShowDeleteConfirm(true)}
                      className="flex-1 py-2 border border-[var(--color-error)]/30 text-[var(--color-error)] rounded-xl text-sm hover:opacity-80 transition-opacity">
                      삭제
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 운영자 답변 */}
          {detail.replies.length > 0 ? (
            <div className="space-y-3">
              {detail.replies.map((reply) => (
                <div key={reply.id} className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[var(--color-accent)]">📢 운영자 답변</span>
                    <span className="text-xs text-[var(--color-text-disabled)]">{formatDateTime(reply.created_at)}</span>
                  </div>
                  <p className="text-sm text-[var(--color-text-primary)] leading-relaxed whitespace-pre-wrap">{reply.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5 text-center">
              <p className="text-[var(--color-text-disabled)] text-sm">아직 운영자 답변이 없습니다.</p>
              <p className="text-[var(--color-text-disabled)] text-xs mt-1">빠른 시일 내에 답변드리겠습니다.</p>
            </div>
          )}
        </>
      ) : null}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-[var(--color-text-primary)] font-bold text-lg text-center mb-2">문의를 삭제할까요?</h2>
            <p className="text-[var(--color-text-tertiary)] text-sm text-center mb-5">삭제된 문의는 복구할 수 없습니다.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 border border-[var(--color-border-default)] text-[var(--color-text-secondary)] rounded-xl text-sm hover:text-[var(--color-text-primary)] transition-colors">
                취소
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2.5 bg-[var(--color-error)] hover:opacity-90 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-opacity">
                {deleting ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── 목록 뷰 ────────────────────────────────────────────────────────────────
export default function MyInquiries() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [list, setList] = useState<InquiryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<InquiryListItem | null>(null)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login', { replace: true }); return }
    loadList()
  }, [isAuthenticated])

  const loadList = async () => {
    setLoading(true)
    try {
      const { data } = await client.get<InquiryListItem[]>('/inquiries/me')
      setList(data)
    } catch {
      toast('목록을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleted = (id: number) => {
    setList((prev) => prev.filter((item) => item.id !== id))
    setSelected(null)
  }

  if (selected) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
        <div className="max-w-lg mx-auto">
          <DetailView
            item={selected}
            onBack={() => setSelected(null)}
            onDeleted={handleDeleted}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-4">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/profile')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">내 문의 내역</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">문의 및 건의 내역을 확인하세요</p>
          </div>
          <div className="ml-auto">
            <button
              onClick={() => navigate('/inquiry/new')}
              className="px-4 py-2 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
            >
              + 새 문의
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : list.length === 0 ? (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-10 text-center">
            <p className="text-4xl mb-3">📭</p>
            <p className="text-[var(--color-text-tertiary)] text-sm mb-4">아직 문의 내역이 없습니다.</p>
            <button
              onClick={() => navigate('/inquiry/new')}
              className="px-5 py-2 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
            >
              문의 작성하기
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {list.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4 hover:border-[var(--color-border-accent)] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm">{CATEGORY_ICON[item.category] ?? ''}</span>
                      <span className="text-xs text-[var(--color-text-tertiary)]">{item.category}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[item.status] ?? STATUS_BADGE['대기']}`}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-[var(--color-text-primary)] text-sm font-medium truncate">{item.title}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1 text-[var(--color-text-disabled)]">
                    <span className="text-xs">{formatDate(item.created_at)}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
